import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import api from '../../api/config';
import { EQUIPMENT_MODELS_RESOURCE } from '../../api/endpoints';
import { toast } from 'react-toastify';
import {
  buildEquipmentModelRelationParams,
  formatEquipmentModelDate,
  getEquipmentModelAuthorLabel,
  getEquipmentModelStatusLabel,
} from './equipmentModelApiHelpers';
import { EQUIPMENT_MODEL_PERMISSIONS } from './equipmentModelPermissions';

const row = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Grid>
);

const ViewEquipmentModel = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(EQUIPMENT_MODELS_RESOURCE.BY_ID(id), {
          params: buildEquipmentModelRelationParams(),
        });
        const payload = data?.data ?? data;
        if (!cancelled) setRecord(payload);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load equipment model');
        navigate('/equipment-models');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading || !record) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/equipment-models')} sx={{ mb: 2 }}>
          Back to equipment models
        </Button>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {record.name ?? 'Equipment Model'}
          </Typography>
          <Can permission={EQUIPMENT_MODEL_PERMISSIONS.EDIT}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/equipment-models/edit/${id}`)}
            >
              Edit
            </Button>
          </Can>
        </Box>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {row('Name', record.name)}
              {row('Status', getEquipmentModelStatusLabel(record))}
              {row('Author', getEquipmentModelAuthorLabel(record))}
              {row('Created at', formatEquipmentModelDate(record.created_at))}
              {row('Description', record.description ?? '—')}
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewEquipmentModel;
